import React, { useState, useEffect } from "react";
import {
  Tabs,
  Tab,
  Typography,
  Avatar,
  Button,
  Collapse,
} from "@material-ui/core";
import ActionButton from "../../components/ActionButton";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import ProfilePage from "./ProfilePage";
import PasswordPage from "./PasswordPage";
import InterfaceSettings from "./InterfaceSettings";
import { useSnackbar } from "notistack";
import ChangeAvatarDialog from "./ChangeAvatarDialog";
import { useSelector, useDispatch } from "react-redux";
import { setUser } from "../../redux/actions/universalActions.js";
import { taxServiceClient, attachmentClient } from "../../AxiosClient";
import cache from "../../CacheClient";

const useStyles = makeStyles((theme) => ({
  body: {
    display: "flex",
    height: "100%",
    backgroundColor: "#fff",
  },
  tabs: {
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  configContainer: {
    padding: 20,
    width: "100%",
    overflow: "auto",
  },
  container: {
    height: "98%",
  },
  tab: {
    textTransform: "none",
  },
  avatarContainer: {
    textAlign: "center",
  },
  profileImage: {
    margin: "5px auto",
    width: 150,
    height: 150,
  },
  header: {
    marginBottom: 15,
    display: "flex",
  },
}));

function UserSettings() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [uploadId, setUploadId] = useState(-1);
  const [saving, setSaving] = useState(false);
  const [updatedUser, setUpdatedUser] = useState({});
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(false);
  const [pictureCollapseOpen, setPictureCollapseOpen] = useState(false);
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.currentUser);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(
    () => {
      if (!uploadId || uploadId === -1) return;
      setUpdatedUser({ ...currentUser, ...updatedUser, avatarURI: uploadId });
      getAvatarContent(uploadId);
    },
    [uploadId]
  );

  useEffect(
    () => {
      if (!currentUser.avatarBase64 && currentUser.avatarURI) {
        getAvatarContent(currentUser.avatarURI);
      }
    },
    [currentUser]
  );

  const saveUser = () => {
    setSaving(true);
    taxServiceClient
      .patch(`/user/${currentUser.userId}`, {
        ...updatedUser,
        password: "",
      })
      .then((res) => {
        updateUserState(res.data);
        enqueueSnackbar(t("user_updated_successfully"), {
          variant: "success",
        });
        setSaving(false);
      })
      .catch((err) => {
        enqueueSnackbar(t("error_updating_user"), {
          variant: "error",
        });
        setSaving(false);
      });
  };

  const updateUserState = (user) => {
    dispatch(setUser({ ...user }));
  };

  const getAvatarContent = (id) => {
    let cached = cache.get(`attachment-${id}`);
    if(!cached){
    attachmentClient
      .get(`/attachment/${id}/content/`, {
        responseType: "blob",
      })
      .then((res) => {
        let reader = new FileReader();
        reader.readAsDataURL(res.data);
        reader.onloadend = () => {
          let base64data = reader.result;
          dispatch(setUser({ ...currentUser, avatarBase64: base64data }));
          cache.put(`attachment-${id}`, base64data);
        };
      })
      .catch((err) => {
        enqueueSnackbar(t("error_retrieving_avatar"), {
          variant: "error",
        });
      });
    } else {
      dispatch(setUser({ ...currentUser, avatarBase64: cached }));
    }
  };

  const profileStateUpdated = (user) => {
    let newUser = Object.assign({}, currentUser, user);
    setUpdatedUser(newUser);
  };

  return (
    <div className={classes.container}>
      <div className={classes.body}>
        <Tabs
          value={selectedTab}
          onChange={(_e, nv) => setSelectedTab(nv)}
          orientation="vertical"
          variant="scrollable"
          className={classes.tabs}
        >
          <Tab
            id="profile-settings"
            className={classes.tab}
            label={t("user_settings_profile")}
          />
          <Tab
            id="password-settings"
            className={classes.tab}
            label={t("user_settings_password")}
          />
          <Tab
            id="interface-settings"
            className={classes.tab}
            label={t("user_settings_interface")}
          />
        </Tabs>
        <div className={classes.configContainer}>
          {selectedTab === 0 && (
            <>
              <div className={classes.header}>
                <Typography variant="h5" style={{ marginRight: 10 }}>
                  {t("user_settings_profile_header")}
                </Typography>
                <ActionButton
                  variant="contained"
                  color="primary"
                  onClick={saveUser}
                  loading={saving}
                  disabled={saveButtonDisabled}
                >
                  {t("global_save")}
                </ActionButton>
              </div>
              <div className={classes.avatarContainer}>
                <Avatar
                  src={currentUser.avatarBase64}
                  className={classes.profileImage}
                  alt="Profile picture"
                />
                <Button
                  onClick={() => setPictureCollapseOpen(!pictureCollapseOpen)}
                >
                  {t("upload_new_profile_picture")}
                </Button>
                <Collapse in={pictureCollapseOpen}>
                  <ChangeAvatarDialog
                    fileUploadStateUpdated={(id) => setUploadId(id)}
                  />
                </Collapse>
              </div>
              <ProfilePage
                currentUser={currentUser}
                stateUpdated={profileStateUpdated}
                saveButtonDisable={(disabled) =>
                  setSaveButtonDisabled(disabled)
                }
              />
            </>
          )}
          {selectedTab === 1 && <PasswordPage currentUser={currentUser} />}
          {selectedTab === 2 && (
            <InterfaceSettings
              currentUser={currentUser}
              updateUserState={updateUserState}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default UserSettings;
